import {Typography} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';

import {getAllIncludes, Insertion} from '../../../../utils/text';

import styles from './custom-suggestion-item.module.css';
import {SearchSuggestion} from './interface';
import {
    getMatchedSynonym,
    isSearchCategory,
    isSearchByDocNameSuggestion,
    isHistoryRecordSuggestion,
    isSearchNode,
    isPrefixSuggestion,
    isSearchByMetadata,
    getMetadataFieldLabel,
    isTagMetadataField,
    getTagVisibilityLabel
} from './suggestion-helpers';

interface Props {
    suggestion: SearchSuggestion;
    searchInput: string;
}

function renderInsertions(insertions: Insertion[], label: string) {
    if (insertions.length) {
        return insertions.map(({prev, current}, labelNdx) => (
            <React.Fragment key={labelNdx}>
                {prev}
                <b>{current}</b>
            </React.Fragment>
        ));
    }

    return label;
}

function getSuggestionPrefix(suggestion: SearchSuggestion, searchInput: string) {
    if (isPrefixSuggestion(suggestion)) {
        return (
            <React.Fragment>
                <span className={styles.bullet}>• </span>
                {renderInsertions(
                    getAllIncludes(searchInput, suggestion.prefix),
                    suggestion.prefix
                )}
            </React.Fragment>
        );
    }

    if (isSearchCategory(suggestion)) {
        return renderInsertions(getAllIncludes(searchInput, 'category'), 'category');
    }

    if (isSearchNode(suggestion)) {
        return renderInsertions(
            getAllIncludes(searchInput, suggestion.nodeType),
            suggestion.nodeType
        );
    }

    if (isHistoryRecordSuggestion(suggestion)) {
        if (suggestion.categoriesSearch) {
            return 'category';
        }

        if (suggestion.referencedNode) {
            return suggestion.referencedNode.type;
        }

        return 'document';
    }

    if (isSearchByMetadata(suggestion)) {
        return getMetadataFieldLabel(suggestion.fieldType);
    }

    return renderInsertions(getAllIncludes(searchInput, suggestion.type), suggestion.type);
}

function getMainLabel(suggestion: SearchSuggestion, searchInput: string) {
    if (isSearchCategory(suggestion) || isSearchNode(suggestion)) {
        return suggestion.name;
    }

    if (isSearchByDocNameSuggestion(suggestion)) {
        return searchInput;
    }

    if (isPrefixSuggestion(suggestion)) {
        return '';
    }

    if (isHistoryRecordSuggestion(suggestion)) {
        if (suggestion.categoriesSearch) {
            return suggestion.categoriesSearch.categories[0].name;
        }

        if (suggestion.referencedNode) {
            return suggestion.referencedNode.name;
        }

        if (suggestion.stringSearch) {
            return suggestion.stringSearch.value;
        }
    }

    if (isSearchByMetadata(suggestion)) {
        return suggestion.displayName || suggestion.value;
    }

    throw new Error(`Unexpected suggestion type: ${suggestion.type}`);
}

function getAdditionalLabel(suggestion: SearchSuggestion) {
    if (isSearchNode(suggestion) && suggestion.taxonName) {
        return `(${suggestion.taxonName})`;
    }

    if (isSearchByMetadata(suggestion) && isTagMetadataField(suggestion.fieldType)) {
        return getTagVisibilityLabel(suggestion.fieldType);
    }

    return undefined;
}

export function CustomSuggestionItem({suggestion, searchInput}: Props) {
    const matchedSynonym = getMatchedSynonym(suggestion);
    const label = getMainLabel(suggestion, searchInput);

    return (
        <div className={styles.root}>
            <div className={classNames(styles.value, {[styles.withSynonym]: matchedSynonym})}>
                <span className={styles.prefix}>
                    {getSuggestionPrefix(suggestion, searchInput)}:{' '}
                </span>

                <span className={styles.label} title={label}>
                    {renderInsertions(getAllIncludes(searchInput, label), label)}
                </span>

                {getAdditionalLabel(suggestion) && (
                    <span className={styles.additional}>{getAdditionalLabel(suggestion)}</span>
                )}
            </div>

            {matchedSynonym && getAllIncludes(searchInput, matchedSynonym) ? (
                <span className={styles.synonym} title={matchedSynonym}>
                    {renderInsertions(getAllIncludes(searchInput, matchedSynonym), matchedSynonym)}
                </span>
            ) : null}

            {suggestion.type === 'document' && (
                <Typography
                    className={styles.placeholder}
                    as="span"
                    intent="quiet"
                    variant="caption"
                >
                    search by document name
                </Typography>
            )}
        </div>
    );
}
